import { ValidationError } from "@models/ValidationError";
import { useAuthContext } from "@auth/context/Auth.context";

export function useFetch(config: NodeJS.ProcessEnv): Client.HttpClient {
  const { token } = useAuthContext();

  const shouldLogHttp =
    config?.NODE_ENV === "development" &&
    String((config as unknown as { SENTINEL_DEBUG_HTTP?: string })?.SENTINEL_DEBUG_HTTP).toLowerCase() === "true";
  
  const baseFetch = async <TRequest, TResponse>(
    method: keyof Client.HttpClient, 
    url: string, 
    options?: Client.RequestOptions<TRequest>
  ): Promise<TResponse> => {
    if (shouldLogHttp && (method === "post" || method === "put") && url.startsWith("/api/process")) {
      console.debug("[HTTP]", method.toUpperCase(), url, options?.body);
    }

    const response = await fetch(`${config?.SENTINEL_API}${url}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      ...options,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      if (response.status === 400) {
        throw new ValidationError(await response.json() as Sentinel.Validation);
      } else {
        const err = await response.text();
        
        console.log(err);
        throw err;
      }
    }

    if (response.status === 200) {
      if (url.startsWith("/api/reports")) {
        return await response.blob() as TResponse;
      }
      return await response.json() as TResponse;
    }
    
    return null;
  };

  return {
    get: <TResponse>(url: string) =>
      baseFetch<never, TResponse>("get", url),
    post: <TRequest, TResponse>(url: string, options?: Client.RequestOptions<TRequest>) => 
      baseFetch<TRequest, TResponse>("post", url, options),
    put: <TRequest, TResponse>(url: string, options?: Client.RequestOptions<TRequest>) => 
      baseFetch<TRequest, TResponse>("put", url, options),
    delete: <TResponse>(url: string) =>
      baseFetch<never, TResponse>("delete", url),
  };
}